import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from '../ui/use-toast';
import { apiRequest } from '../../config/api';
import { X, Check } from 'lucide-react';
import DentalExaminationForm from './DentalExaminationForm';

const MedicalHistoryForm = ({ open, onClose, patientId, patientName, appointmentId, onNext, hideDialog = false }) => {
  const [loading, setLoading] = useState(false);
  const [showDentalExam, setShowDentalExam] = useState(false);
  const [formData, setFormData] = useState({
    surgery_hospitalized: 'No',
    surgery_details: '',
    fever_cold_cough: 'No',
    symptoms_details: '',
    medical_problems: [],
    abnormal_bleeding: 'No',
    abnormal_bleeding_details: '',
    taking_medicine: 'No',
    medicine_details: '',
    allergic_medication: 'No',
    allergy_details: '',
    past_dental_history: ''
  });
  const [errors, setErrors] = useState({});

  const medicalProblemsOptions = [
    'Artificial Valves / Pacemakers',
    'Asthma',
    'Allergy',
    'Bleeding Disorder',
    'Epilepsy / Seizure',
    'Heart Disease',
    'Hypertension / Hypotension',
    'Hormone Disorder',
    'Jaundice / Liver Disease',
    'Stomach Ulcer',
    'Low / High Pressure',
    'Arthritis / Joint Problem',
    'Kidney Problems',
    'Thyroid Problems',
    'Others'
  ];

  useEffect(() => {
    if (open && patientId) {
      fetchMedicalHistory();
    } else if (!open) {
      // Reset form when dialog closes
      setFormData({
        surgery_hospitalized: 'No',
        surgery_details: '',
        fever_cold_cough: 'No',
        symptoms_details: '',
        medical_problems: [],
        abnormal_bleeding: 'No',
        abnormal_bleeding_details: '',
        taking_medicine: 'No',
        medicine_details: '',
        allergic_medication: 'No',
        allergy_details: '',
        past_dental_history: ''
      });
      setErrors({});
    }
  }, [open, patientId]);

  const fetchMedicalHistory = async () => {
    try {
      const url = appointmentId 
        ? `get_medical_history.php?patient_id=${patientId}&appointment_id=${appointmentId}`
        : `get_medical_history.php?patient_id=${patientId}`;
      const data = await apiRequest(url);
      if (data.success && data.medical_history) {
        // Only set formData if medical_history exists and is not null
        setFormData({
          surgery_hospitalized: data.medical_history.surgery_hospitalized || 'No',
          surgery_details: data.medical_history.surgery_details || '',
          fever_cold_cough: data.medical_history.fever_cold_cough || 'No',
          symptoms_details: data.medical_history.symptoms_details || '',
          medical_problems: data.medical_history.medical_problems || [],
          abnormal_bleeding: data.medical_history.abnormal_bleeding || 'No',
          abnormal_bleeding_details: data.medical_history.abnormal_bleeding_details || '',
          taking_medicine: data.medical_history.taking_medicine || 'No',
          medicine_details: data.medical_history.medicine_details || '',
          allergic_medication: data.medical_history.allergic_medication || 'No',
          allergy_details: data.medical_history.allergy_details || '',
          past_dental_history: data.medical_history.past_dental_history || ''
        });
      } else {
        // No medical history found - reset to defaults
        setFormData({
          surgery_hospitalized: 'No',
          surgery_details: '',
          fever_cold_cough: 'No',
          symptoms_details: '',
          medical_problems: [],
          abnormal_bleeding: 'No',
          abnormal_bleeding_details: '',
          taking_medicine: 'No',
          medicine_details: '',
          allergic_medication: 'No',
          allergy_details: '',
          past_dental_history: ''
        });
      }
      setErrors({});
    } catch (error) {
      console.error('Error fetching medical history:', error);
      // On error, reset to defaults
      setFormData({
        surgery_hospitalized: 'No',
        surgery_details: '',
        fever_cold_cough: 'No',
        symptoms_details: '',
        medical_problems: [],
        abnormal_bleeding: 'No',
        abnormal_bleeding_details: '',
        taking_medicine: 'No',
        medicine_details: '',
        allergic_medication: 'No',
        allergy_details: '',
        past_dental_history: ''
      });
      setErrors({});
    }
  };

  const handleRadioChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProblemToggle = (problem) => {
    setFormData(prev => {
      const problems = prev.medical_problems || [];
      if (problems.includes(problem)) {
        return { ...prev, medical_problems: problems.filter(p => p !== problem) };
      } else {
        return { ...prev, medical_problems: [...problems, problem] };
      }
    });
  };

  const handleSaveDraft = async () => {
    setLoading(true);

    try {
      const data = await apiRequest('add_medical_history.php', {
        method: 'POST',
        body: JSON.stringify({
          patient_id: patientId,
          appointment_id: appointmentId || null,
          ...formData,
          is_draft: true // Flag to skip validation
        })
      });

      if (data.success) {
        toast({
          title: 'Draft Saved',
          description: 'Your progress has been saved. You can continue later.',
          variant: 'default'
        });
      } else {
        toast({
          title: 'Error',
          description: data.error || 'Failed to save draft',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save draft',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all mandatory details fields when "Yes" is selected
    const newErrors = {};
    
    // Question 1: Surgery details
    if (formData.surgery_hospitalized === 'Yes' && !formData.surgery_details.trim()) {
      newErrors.surgery_details = 'Please provide details';
    }
    
    // Question 2: Symptoms details
    if (formData.fever_cold_cough === 'Yes' && !formData.symptoms_details.trim()) {
      newErrors.symptoms_details = 'Please describe your symptom';
    }
    
    // Question 4: Abnormal bleeding details
    if (formData.abnormal_bleeding === 'Yes' && !formData.abnormal_bleeding_details.trim()) {
      newErrors.abnormal_bleeding_details = 'Please provide details';
    }
    
    // Question 5: Medicine details
    if (formData.taking_medicine === 'Yes' && !formData.medicine_details.trim()) {
      newErrors.medicine_details = 'Please provide details';
    }
    
    // Question 6: Allergy details
    if (formData.allergic_medication === 'Yes' && !formData.allergy_details.trim()) {
      newErrors.allergy_details = 'Please provide details';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }
    
    setErrors({});
    setLoading(true);

    try {
      const data = await apiRequest('add_medical_history.php', {
        method: 'POST',
        body: JSON.stringify({
          patient_id: patientId,
          appointment_id: appointmentId || null,
          ...formData,
          is_draft: false // Final submission
        })
      });

      if (data.success) {
        toast({
          title: 'Success',
          description: 'Medical history saved successfully',
          variant: 'default'
        });
        // Validate patientId before proceeding
        if (!patientId) {
          toast({
            title: 'Error',
            description: 'Patient ID is missing. Cannot proceed to dental examination.',
            variant: 'destructive'
          });
          setLoading(false);
          return;
        }
        
        // If hideDialog is true, we're in a wizard - call onNext with data
        if (hideDialog && onNext) {
          onNext(formData);
        } else {
          // Open Dental Examination form without closing Medical History
          console.log('MedicalHistoryForm: Proceeding to Dental Examination with patientId:', patientId);
          setShowDentalExam(true);
        }
      } else {
        toast({
          title: 'Error',
          description: data.error || 'Failed to save medical history',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save medical history',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      surgery_hospitalized: 'No',
      surgery_details: '',
      fever_cold_cough: 'No',
      symptoms_details: '',
      medical_problems: [],
      abnormal_bleeding: 'No',
      abnormal_bleeding_details: '',
      taking_medicine: 'No',
      medicine_details: '',
      allergic_medication: 'No',
      allergy_details: '',
      past_dental_history: ''
    });
    setErrors({});
  };

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-6">
          {/* Question 1: Surgery or hospitalized */}
          <div className="space-y-3">
            <Label className="text-white text-sm sm:text-base">
              1. Surgery or hospitalized in the past 5 years?
            </Label>
            <div className="flex gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="surgery_hospitalized"
                  value="Yes"
                  checked={formData.surgery_hospitalized === 'Yes'}
                  onChange={(e) => {
                    handleRadioChange('surgery_hospitalized', e.target.value);
                    // Clear error when changing selection
                    if (errors.surgery_details) {
                      setErrors(prev => ({ ...prev, surgery_details: '' }));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                />
                <span className="text-white">Yes</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="surgery_hospitalized"
                  value="No"
                  checked={formData.surgery_hospitalized === 'No'}
                  onChange={(e) => {
                    handleRadioChange('surgery_hospitalized', e.target.value);
                    // Clear error and details when changing to No
                    setFormData(prev => ({ ...prev, surgery_details: '' }));
                    if (errors.surgery_details) {
                      setErrors(prev => ({ ...prev, surgery_details: '' }));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                />
                <span className="text-white">No</span>
              </label>
            </div>
            {formData.surgery_hospitalized === 'Yes' && (
              <div className="space-y-2">
                <Label className="text-white text-sm">Procedure details *</Label>
                <Textarea
                  value={formData.surgery_details}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, surgery_details: e.target.value }));
                    // Clear error when user starts typing
                    if (errors.surgery_details) {
                      setErrors(prev => ({ ...prev, surgery_details: '' }));
                    }
                  }}
                  placeholder="Specify procedure, month/year, and reason"
                  className={`bg-gray-800 border-gray-600 text-white ${
                    errors.surgery_details ? 'border-red-500' : ''
                  }`}
                  rows={3}
                  required
                />
                {errors.surgery_details && (
                  <p className="text-red-400 text-xs sm:text-sm">{errors.surgery_details}</p>
                )}
              </div>
            )}
          </div>

          {/* Question 2: Fever/cold/cough */}
          <div className="space-y-3">
            <Label className="text-white text-sm sm:text-base">
              2. Are you suffering from fever/cold/cough?
            </Label>
            <div className="flex gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="fever_cold_cough"
                  value="Yes"
                  checked={formData.fever_cold_cough === 'Yes'}
                  onChange={(e) => {
                    handleRadioChange('fever_cold_cough', e.target.value);
                    // Clear error when changing selection
                    if (errors.symptoms_details) {
                      setErrors(prev => ({ ...prev, symptoms_details: '' }));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                />
                <span className="text-white">Yes</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="fever_cold_cough"
                  value="No"
                  checked={formData.fever_cold_cough === 'No'}
                  onChange={(e) => {
                    handleRadioChange('fever_cold_cough', e.target.value);
                    // Clear error and symptoms_details when changing to No
                    setFormData(prev => ({ ...prev, symptoms_details: '' }));
                    if (errors.symptoms_details) {
                      setErrors(prev => ({ ...prev, symptoms_details: '' }));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                />
                <span className="text-white">No</span>
              </label>
            </div>
            {formData.fever_cold_cough === 'Yes' && (
              <div className="space-y-2">
                <Label className="text-white text-sm">Symptoms details *</Label>
                <Textarea
                  value={formData.symptoms_details}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, symptoms_details: e.target.value }));
                    // Clear error when user starts typing
                    if (errors.symptoms_details) {
                      setErrors(prev => ({ ...prev, symptoms_details: '' }));
                    }
                  }}
                  placeholder="Please describe your symptoms"
                  className={`bg-gray-800 border-gray-600 text-white ${
                    errors.symptoms_details ? 'border-red-500' : ''
                  }`}
                  rows={3}
                  required
                />
                {errors.symptoms_details && (
                  <p className="text-red-400 text-xs sm:text-sm">{errors.symptoms_details}</p>
                )}
              </div>
            )}
          </div>

          {/* Question 3: Medical problems */}
          <div className="space-y-3">
            <Label className="text-white text-sm sm:text-base">
              3. Do you have any of the following problems? (select all that apply)
            </Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {medicalProblemsOptions.map((problem) => (
                <button
                  key={problem}
                  type="button"
                  onClick={() => handleProblemToggle(problem)}
                  className={`px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                    formData.medical_problems.includes(problem)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-600'
                  }`}
                >
                  {formData.medical_problems.includes(problem) && (
                    <Check className="h-3 w-3 inline mr-1" />
                  )}
                  {problem}
                </button>
              ))}
            </div>
          </div>

          {/* Question 4: Abnormal bleeding */}
          <div className="space-y-3">
            <Label className="text-white text-sm sm:text-base">
              4. Any history of abnormal bleeding following injury or operation?
            </Label>
            <div className="flex gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="abnormal_bleeding"
                  value="Yes"
                  checked={formData.abnormal_bleeding === 'Yes'}
                  onChange={(e) => {
                    handleRadioChange('abnormal_bleeding', e.target.value);
                    // Clear error when changing selection
                    if (errors.abnormal_bleeding_details) {
                      setErrors(prev => ({ ...prev, abnormal_bleeding_details: '' }));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                />
                <span className="text-white">Yes</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="abnormal_bleeding"
                  value="No"
                  checked={formData.abnormal_bleeding === 'No'}
                  onChange={(e) => {
                    handleRadioChange('abnormal_bleeding', e.target.value);
                    // Clear error and details when changing to No
                    setFormData(prev => ({ ...prev, abnormal_bleeding_details: '' }));
                    if (errors.abnormal_bleeding_details) {
                      setErrors(prev => ({ ...prev, abnormal_bleeding_details: '' }));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                />
                <span className="text-white">No</span>
              </label>
            </div>
            {formData.abnormal_bleeding === 'Yes' && (
              <div className="space-y-2">
                <Label className="text-white text-sm">Please provide details *</Label>
                <Textarea
                  value={formData.abnormal_bleeding_details}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, abnormal_bleeding_details: e.target.value }));
                    // Clear error when user starts typing
                    if (errors.abnormal_bleeding_details) {
                      setErrors(prev => ({ ...prev, abnormal_bleeding_details: '' }));
                    }
                  }}
                  placeholder="Please provide details about abnormal bleeding"
                  className={`bg-gray-800 border-gray-600 text-white ${
                    errors.abnormal_bleeding_details ? 'border-red-500' : ''
                  }`}
                  rows={3}
                  required
                />
                {errors.abnormal_bleeding_details && (
                  <p className="text-red-400 text-xs sm:text-sm">{errors.abnormal_bleeding_details}</p>
                )}
              </div>
            )}
          </div>

          {/* Question 5: Taking medicine */}
          <div className="space-y-3">
            <Label className="text-white text-sm sm:text-base">
              5. Are you taking any medicine?
            </Label>
            <div className="flex gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="taking_medicine"
                  value="Yes"
                  checked={formData.taking_medicine === 'Yes'}
                  onChange={(e) => {
                    handleRadioChange('taking_medicine', e.target.value);
                    // Clear error when changing selection
                    if (errors.medicine_details) {
                      setErrors(prev => ({ ...prev, medicine_details: '' }));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                />
                <span className="text-white">Yes</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="taking_medicine"
                  value="No"
                  checked={formData.taking_medicine === 'No'}
                  onChange={(e) => {
                    handleRadioChange('taking_medicine', e.target.value);
                    // Clear error and details when changing to No
                    setFormData(prev => ({ ...prev, medicine_details: '' }));
                    if (errors.medicine_details) {
                      setErrors(prev => ({ ...prev, medicine_details: '' }));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                />
                <span className="text-white">No</span>
              </label>
            </div>
            {formData.taking_medicine === 'Yes' && (
              <div className="space-y-2">
                <Label className="text-white text-sm">Please provide details *</Label>
                <Textarea
                  value={formData.medicine_details}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, medicine_details: e.target.value }));
                    // Clear error when user starts typing
                    if (errors.medicine_details) {
                      setErrors(prev => ({ ...prev, medicine_details: '' }));
                    }
                  }}
                  placeholder="Specify medicines you are taking"
                  className={`bg-gray-800 border-gray-600 text-white ${
                    errors.medicine_details ? 'border-red-500' : ''
                  }`}
                  rows={3}
                  required
                />
                {errors.medicine_details && (
                  <p className="text-red-400 text-xs sm:text-sm">{errors.medicine_details}</p>
                )}
              </div>
            )}
          </div>

          {/* Question 6: Allergic to medication */}
          <div className="space-y-3">
            <Label className="text-white text-sm sm:text-base">
              6. Allergic to any medication?
            </Label>
            <div className="flex gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="allergic_medication"
                  value="Yes"
                  checked={formData.allergic_medication === 'Yes'}
                  onChange={(e) => {
                    handleRadioChange('allergic_medication', e.target.value);
                    // Clear error when changing selection
                    if (errors.allergy_details) {
                      setErrors(prev => ({ ...prev, allergy_details: '' }));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                />
                <span className="text-white">Yes</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="allergic_medication"
                  value="No"
                  checked={formData.allergic_medication === 'No'}
                  onChange={(e) => {
                    handleRadioChange('allergic_medication', e.target.value);
                    // Clear error and details when changing to No
                    setFormData(prev => ({ ...prev, allergy_details: '' }));
                    if (errors.allergy_details) {
                      setErrors(prev => ({ ...prev, allergy_details: '' }));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                />
                <span className="text-white">No</span>
              </label>
            </div>
            {formData.allergic_medication === 'Yes' && (
              <div className="space-y-2">
                <Label className="text-white text-sm">Please provide details *</Label>
                <Textarea
                  value={formData.allergy_details}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, allergy_details: e.target.value }));
                    // Clear error when user starts typing
                    if (errors.allergy_details) {
                      setErrors(prev => ({ ...prev, allergy_details: '' }));
                    }
                  }}
                  placeholder="Specify medications you are allergic to"
                  className={`bg-gray-800 border-gray-600 text-white ${
                    errors.allergy_details ? 'border-red-500' : ''
                  }`}
                  rows={3}
                  required
                />
                {errors.allergy_details && (
                  <p className="text-red-400 text-xs sm:text-sm">{errors.allergy_details}</p>
                )}
              </div>
            )}
          </div>

          {/* Question 7: Past dental history */}
          <div className="space-y-3">
            <Label className="text-white text-sm sm:text-base">
              7. Past dental history
            </Label>
            <Textarea
              value={formData.past_dental_history}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 500) {
                  setFormData(prev => ({ ...prev, past_dental_history: value }));
                }
              }}
              placeholder="Previous treatments, frequency of visits, any complications"
              className="bg-gray-800 border-gray-600 text-white"
              rows={5}
            />
            <p className="text-gray-400 text-xs sm:text-sm text-right">
              {formData.past_dental_history.length}/500
            </p>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="w-full sm:w-auto bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
            >
              <X className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                className="flex-1 sm:flex-none bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
              >
                Reset
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                disabled={loading}
                className="flex-1 sm:flex-none bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
              >
                {loading ? 'Saving...' : 'Save Draft'}
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 sm:flex-none medical-gradient text-white hover:opacity-90"
              >
                {loading ? 'Saving...' : 'Next'}
              </Button>
            </div>
          </DialogFooter>
        </form>
  );

  // If hideDialog is true, return only form content (for wizard)
  if (hideDialog) {
    return formContent;
  }

  // Otherwise, return with Dialog wrapper
  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-effect border-gray-700 bg-gray-900/95 backdrop-blur-sm max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw]">
        <DialogHeader>
          <DialogTitle className="text-white text-xl sm:text-2xl">Medical History</DialogTitle>
          <DialogDescription className="text-gray-400">
            Please provide your complete medical history for safe treatment planning.
          </DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>

    {/* Dental Examination Form - Separate Dialog */}
    {!hideDialog && (
      <DentalExaminationForm
        open={showDentalExam && !!patientId}
        onClose={() => {
          setShowDentalExam(false);
        }}
        patientId={patientId}
        patientName={patientName}
        onBack={() => {
          setShowDentalExam(false);
        }}
        onComplete={(data) => {
          setShowDentalExam(false);
          if (data?.type === 'showReview' && onNext) {
            onNext(data);
          }
        }}
        hideDialog={false}
      />
    )}
    </>
  );
};

export default MedicalHistoryForm;

